import { VStack, HStack, Box } from "@chakra-ui/react";
import { useCallback } from "react";
import { MeetingEventsFilters } from "../hooks/filters";
import WeekNavigator from "./WeekNavigator";
import { TabOption, TabSelector } from "components/TabSelector";
import FilterButton from "components/Button/FilterButton";
import { MeetingType } from "features/Resources/Meeting/models";

interface MeetingEventsFiltersProps {
  filters: MeetingEventsFilters;
  onFiltersChange: (filters: MeetingEventsFilters) => void;
}

function FilterTabs({ options, value, onChange }: {
  options: { label: string, value: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Box w="fit-content">
      <TabSelector size="sm" value={value} onChange={onChange}>
        {options.map((option) => (
          <TabOption key={option.value} value={option.value}>{option.label}</TabOption>
        ))}
      </TabSelector>
    </Box>
  );
}

const scheduledStatusOptions = [
  { label: "Scheduled", value: "scheduled" },
  { label: "Unscheduled", value: "pending_scheduling" },
];

const meetingTypeButtons: { label: string; value: MeetingType }[] = [
  { label: "Individual", value: "individual" },
  { label: "Group (Ad-hoc)", value: "group_session_adhoc" },
  { label: "Group (Recurring)", value: "group_session_recurring" },
];

export default function MeetingEventsFiltersComponent({
  filters,
  onFiltersChange,
}: MeetingEventsFiltersProps) {
  const handleFilterChange = useCallback((key: keyof MeetingEventsFilters, value: string | undefined) => {
    const newValue = value === "" ? undefined : value;
    if (filters[key] !== newValue) {
      onFiltersChange({ ...filters, [key]: newValue });
    }
  }, [filters, onFiltersChange]);

  const handleWeekChange = useCallback((startTime: string, endTime: string) => {
    onFiltersChange({ ...filters, start_time: startTime, end_time: endTime });
  }, [filters, onFiltersChange]);

  const toggleMeetingType = useCallback((type: MeetingType) => {
    handleFilterChange("meeting_type", filters.meeting_type === type ? undefined : type);
  }, [filters.meeting_type, handleFilterChange]);

  return (
    <VStack spacing={3} align="stretch">
      <FilterTabs
        options={scheduledStatusOptions}
        value={filters.scheduled_status}
        onChange={(value) => handleFilterChange("scheduled_status", value)}
      />
      <HStack justifyContent="space-between" flexWrap="wrap" gap={3}>
        <HStack spacing={2} flexWrap="wrap">
          {meetingTypeButtons.map(({ label, value }) => (
            <FilterButton
              key={value}
              isActive={filters.meeting_type === value}
              onClick={() => toggleMeetingType(value)}
              size="sm"
            >
              {label}
            </FilterButton>
          ))}
        </HStack>
        {filters.scheduled_status === "scheduled" && (
          <WeekNavigator
            startTime={filters.start_time}
            endTime={filters.end_time}
            onWeekChange={handleWeekChange}
          />
        )}
      </HStack>
    </VStack>
  );
}
